import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, UserPlus, Phone, History, Trash2 } from "lucide-react";
import { api, formatApiError, brl } from "@/lib/api";
import { ContextMenu } from "@/components/ContextMenu";

const norm = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

export default function Clientes() {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [history, setHistory] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", notes: "" });
  const [menu, setMenu] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchClients = () => api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  useEffect(() => { fetchClients(); }, []);

  const filtered = clients.filter(
    (c) => norm(c.name).includes(norm(search)) || (c.phone || "").includes(search)
  );

  const selectClient = async (c) => {
    setSelected(c);
    setForm({ name: c.name, phone: c.phone || "", notes: c.notes || "" });
    setHistory(null);
    try {
      const { data } = await api.get(`/clients/${c.id}/history`);
      setHistory(data);
    } catch {
      // ignore
    }
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Informe o nome do cliente");
      return;
    }
    setBusy(true);
    try {
      if (selected) {
        await api.put(`/clients/${selected.id}`, form);
        toast.success("Cliente atualizado");
      } else {
        await api.post("/clients", form);
        toast.success("Cliente cadastrado");
      }
      fetchClients();
      setSelected(null);
      setForm({ name: "", phone: "", notes: "" });
      setHistory(null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Excluir o cliente "${c.name}"?`)) return;
    try {
      await api.delete(`/clients/${c.id}`);
      toast.success("Cliente excluído");
      if (selected?.id === c.id) {
        setSelected(null);
        setForm({ name: "", phone: "", notes: "" });
        setHistory(null);
      }
      fetchClients();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const field = "w-full rounded-lg border border-stone-300 px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#C8A279] focus:ring-2 focus:ring-[#C8A279]/20 transition-all";

  return (
    <div data-testid="clientes-page">
      <h1 className="font-display text-3xl sm:text-4xl font-light mb-6">Clientes</h1>
      <div className="grid lg:grid-cols-2 gap-6">
        {/* List */}
        <div className="bg-white border border-stone-200 rounded-2xl p-5 shadow-[0_2px_12px_rgba(0,0,0,0.03)] flex flex-col max-h-[75vh]">
          <div className="relative mb-4">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nome ou telefone…"
              className="w-full rounded-full border border-stone-300 pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-[#C8A279] transition-all"
              data-testid="clientes-search-input"
            />
          </div>
          <button
            onClick={() => { setSelected(null); setForm({ name: "", phone: "", notes: "" }); setHistory(null); }}
            className="mb-3 inline-flex items-center gap-2 text-sm font-medium text-[#B38F68] hover:text-[#121212] transition-colors w-fit"
            data-testid="clientes-new-button"
          >
            <UserPlus size={15} /> {search && !filtered.length ? `Cadastrar "${search}"` : "Novo cliente"}
          </button>
          <div className="flex-1 overflow-y-auto thin-scroll space-y-2">
            {filtered.length === 0 && <p className="text-sm text-stone-400 py-6 text-center">Nenhum cliente encontrado. Cadastre um novo acima.</p>}
            {filtered.map((c) => (
              <div
                key={c.id}
                onClick={() => selectClient(c)}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setMenu({ x: e.clientX, y: e.clientY, client: c });
                }}
                className={`flex items-center justify-between px-4 py-3 rounded-xl border cursor-pointer transition-colors ${
                  selected?.id === c.id ? "bg-[#F3EFE6] border-[#C8A279]" : "border-stone-100 hover:border-stone-300"
                }`}
                data-testid={`cliente-row-${c.id}`}
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{c.name}</p>
                  {c.phone ? <p className="text-xs text-stone-400 flex items-center gap-1"><Phone size={11} /> {c.phone}</p> : null}
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); remove(c); }}
                  className="p-2 rounded-full text-stone-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                  data-testid={`cliente-delete-${c.id}`}
                  aria-label={`Excluir ${c.name}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Form */}
        <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-[0_2px_12px_rgba(0,0,0,0.03)] max-h-[75vh] overflow-y-auto thin-scroll">
          <p className="text-[10px] font-semibold tracking-[0.2em] uppercase text-[#C8A279] mb-1">
            {selected ? "Alterar cliente" : "Cadastro"}
          </p>
          <h2 className="font-display text-2xl mb-5" data-testid="cliente-form-title">
            {selected ? selected.name : "Novo cliente"}
          </h2>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold tracking-[0.15em] uppercase text-stone-500">Nome</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={`mt-1.5 ${field}`} data-testid="cliente-name-input" />
            </div>
            <div>
              <label className="text-xs font-semibold tracking-[0.15em] uppercase text-stone-500">Telefone</label>
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(41) 9…" className={`mt-1.5 ${field}`} data-testid="cliente-phone-input" />
            </div>
            <div>
              <label className="text-xs font-semibold tracking-[0.15em] uppercase text-stone-500">Observações</label>
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className={`mt-1.5 ${field}`} data-testid="cliente-notes-input" />
            </div>
            <div className="flex gap-3 pt-1">
              <button onClick={save} disabled={busy} className="px-7 py-3 rounded-full bg-[#121212] text-white text-sm font-medium hover:bg-stone-700 transition-colors disabled:opacity-50" data-testid="cliente-save-button">
                {selected ? "Salvar alterações" : "Cadastrar"}
              </button>
              {selected && (
                <button onClick={() => { setSelected(null); setForm({ name: "", phone: "", notes: "" }); setHistory(null); }} className="px-6 py-3 rounded-full border border-stone-300 text-sm text-stone-600 hover:border-stone-900 transition-colors" data-testid="cliente-clear-button">
                  Limpar
                </button>
              )}
            </div>
          </div>

          {/* History */}
          {selected && (
            <div className="mt-8 pt-6 border-t border-stone-200">
              <p className="text-xs font-semibold tracking-[0.15em] uppercase text-stone-500 flex items-center gap-2 mb-4">
                <History size={14} className="text-[#C8A279]" /> Histórico do cliente
              </p>
              {!history && <p className="text-sm text-stone-400">Carregando histórico…</p>}
              {history && (
                <div className="space-y-2" data-testid="cliente-history-list">
                  {history.debt_total > 0 && (
                    <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-2.5">
                      Dívida pendente (fiado): <strong data-testid="cliente-debt-amount">{brl(history.debt_total)}</strong>
                    </p>
                  )}
                  {history.sales.length === 0 && history.appointments.length === 0 && (
                    <p className="text-sm text-stone-400">Nenhum registro ainda.</p>
                  )}
                  {history.sales.map((s) => (
                    <div key={s.id} className="flex items-center justify-between border border-stone-100 rounded-lg px-4 py-2.5 text-sm">
                      <div>
                        <p className="font-medium">{s.items.map((i) => i.name).join(", ")}</p>
                        <p className="text-xs text-stone-400">
                          {new Date(s.sale_date + "T12:00:00").toLocaleDateString("pt-BR")} · {s.status}{s.status === "fiado" && !s.paid ? " (pendente)" : ""}
                        </p>
                      </div>
                      <p className="font-semibold">{brl(s.total)}</p>
                    </div>
                  ))}
                  {history.appointments.map((a) => (
                    <div key={`a-${a.id}`} className="flex items-center justify-between border border-dashed border-stone-200 rounded-lg px-4 py-2.5 text-sm text-stone-500">
                      <div>
                        <p className="font-medium text-stone-600">{a.service || "Agendamento"}</p>
                        <p className="text-xs">{new Date(a.date + "T12:00:00").toLocaleDateString("pt-BR")} · {a.time} · {a.status}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {menu && (
        <ContextMenu
          x={menu.x}
          y={menu.y}
          onClose={() => setMenu(null)}
          items={[
            { label: "Alterar", testId: "alterar", action: () => selectClient(menu.client) },
            { label: "Excluir", testId: "excluir", danger: true, action: () => remove(menu.client) },
          ]}
        />
      )}
    </div>
  );
}
